import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { settingsPage } from './components/settings/SettingsPage';

import { AlertDisplay, OAuthRequestDialog } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';
import { ConfigBasedSignInPage } from './components/ConfigBasedSignInPage';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { CalculatedInputExtension } from './scaffolder/CalculatedInput';
import { ValidateKebabCaseFieldExtension } from './scaffolder/ValidateKebabCase/extensions';
import {SelectAdoProjectsFieldExtension } from './scaffolder/SelectAdoProjects/extensions';
import {SelectAdoProjectFieldExtension} from './scaffolder/SelectAdoProject/extensions';
import {SelectProjectNameFieldExtension} from './scaffolder/SelectProjectName/extensions';
import {SelectAdoProductFieldExtension} from './scaffolder/SelectAdoProduct/extensions';
import {CreateAdoProductFieldExtension} from './scaffolder/CreateAdoProduct/extensions';
import {CreateAdoProjectFieldExtension} from './scaffolder/CreateAdoProject/extensions';
import {CreateAdoAppFieldExtension} from './scaffolder/CreateAdoApp/extensions';
import {SelectAdoAppFieldExtension} from './scaffolder/SelectAdoApp/extensions';
import {ReplitAppFieldExtension} from './scaffolder/ReplitApp/extensions';
import {SelectAdoRegionFieldExtension} from './scaffolder/SelectAdoRegion/extensions';
import {SelectUndeployProductFieldExtension} from './scaffolder/SelectUndeployProduct/extensions';
import {SelectUndeployAppsFieldExtension} from './scaffolder/SelectUndeployApps/extensions';
import { CreateApiGatewayFieldExtension } from './scaffolder/CreateApiGateway';
import { CreateLoadBalancerExtension } from './scaffolder/CreateLoadBalancer';
import { CreateRedisExtension } from './scaffolder/CreateRedis';
import { CreateRdsExtension } from './scaffolder/CreateRds';
import { CreateOpensearchExtension } from './scaffolder/CreateOpensearch';
import { OauthForApisFieldExtension } from './scaffolder/OauthProvider/extensions';
import AskGPTChatbox from './components/AskGPT';
import { ProjectsPage } from './components/projects/projectsPage';
import { ProjectDetailPage } from './components/projects/projectDetails';
import { ProductDetailPage } from './components/projects/productDetails';
import { TfModulesPage } from './components/tfmodules/tfModulesPage';
import { ProjectEnvConfigExtension } from './scaffolder/ProjectEnvConfig';
import { FileUploadExtension } from './scaffolder/FileUpload';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: ConfigBasedSignInPage,
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/projects" element={<ProjectsPage />} />
    <Route path="/projects/:projectName" element={<ProjectDetailPage />} />
    <Route path="/projects/:projectName/:productName" element={<ProductDetailPage />} />
    <Route path="/tfmodules" element={<TfModulesPage />} />
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <CalculatedInputExtension />
        <ValidateKebabCaseFieldExtension />
        <SelectAdoProjectsFieldExtension />
        <SelectAdoProjectFieldExtension />
        <SelectProjectNameFieldExtension />
        <SelectAdoProductFieldExtension />
        <CreateAdoProductFieldExtension />
        <SelectAdoRegionFieldExtension />
        <SelectUndeployProductFieldExtension />
        <SelectUndeployAppsFieldExtension />
        <CreateAdoAppFieldExtension />
        <SelectAdoAppFieldExtension />
        <ReplitAppFieldExtension />
        <CreateApiGatewayFieldExtension />
        <OauthForApisFieldExtension />
        <ProjectEnvConfigExtension />
        <CreateAdoProjectFieldExtension />
        <CreateLoadBalancerExtension />
        <CreateRedisExtension />
        <CreateRdsExtension />
        <CreateOpensearchExtension />
        <FileUploadExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route 
      path="/create/edit" 
      element={
        <RequirePermission permission={taskCreatePermission}>
          <ScaffolderPage />
        </RequirePermission>
      }
    >
      <ScaffolderFieldExtensions>
      <CalculatedInputExtension />
        <ValidateKebabCaseFieldExtension />
        <SelectAdoProjectsFieldExtension />
        <SelectAdoProjectFieldExtension />
        <SelectProjectNameFieldExtension />
        <SelectAdoProductFieldExtension />
        <CreateAdoProductFieldExtension />
        <SelectAdoRegionFieldExtension />
        <SelectUndeployProductFieldExtension />
        <SelectUndeployAppsFieldExtension />
        <CreateAdoAppFieldExtension />
        <SelectAdoAppFieldExtension />
        <ReplitAppFieldExtension />
        <CreateApiGatewayFieldExtension />
        <OauthForApisFieldExtension />
        <ProjectEnvConfigExtension />
        <CreateAdoProjectFieldExtension />
        <CreateLoadBalancerExtension />
        <CreateRedisExtension />
        <CreateRdsExtension />
        <CreateOpensearchExtension />
        <FileUploadExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      {settingsPage}
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
    <AskGPTChatbox />
  </>,
);
